import { Button } from "@/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
} from "@/components/dialog"
import { NfpmPendingNotice } from "@/domains/thick-v1/components/nfpm-pending-notice"
import { cn, dollarsPrettified } from "@/domains/library"

export type BurnPositionViewProps = {
  token0Symbol: string
  token1Symbol: string
  /** Full pre-formatted withdrawable amounts (liquidity + fees combined). */
  amount0Display: string
  amount1Display: string
  positionValueUsd: number
  claimableFeesUsd: number
  slippagePercent: number
  confirmed: boolean
  onToggleConfirmed: (next: boolean) => void
  canSubmit: boolean
  isSubmitting: boolean
  submissionError?: string
  confirmDialogOpen: boolean
  onChangeConfirmDialogOpen: (open: boolean) => void
  onSubmit: () => void
  /** See `CollectFeesViewProps.nfpmTokenIdPending`. */
  nfpmTokenIdPending?: boolean
}

/**
 * Pure "close position" view.
 *
 * Closing is destructive: it removes all liquidity, collects all fees, and
 * burns the NFP. The view therefore gates submission behind an explicit
 * checkbox confirmation AND a modal confirmation dialog.
 */
export const BurnPositionView = ({
  token0Symbol,
  token1Symbol,
  amount0Display,
  amount1Display,
  positionValueUsd,
  claimableFeesUsd,
  slippagePercent,
  confirmed,
  onToggleConfirmed,
  canSubmit,
  isSubmitting,
  submissionError,
  confirmDialogOpen,
  onChangeConfirmDialogOpen,
  onSubmit,
  nfpmTokenIdPending = false,
}: BurnPositionViewProps) => {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-p400-light bg-p400 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-t100 text-base font-medium">Close position</h2>
        <span className="text-t300 text-xs">Slippage: {slippagePercent}%</span>
      </div>

      <p className="text-t300 text-xs">
        Closing this position will remove all liquidity, collect all accrued fees, and burn the
        position NFT. This action cannot be undone.
      </p>

      <div className="grid grid-cols-2 gap-2">
        <SummaryStat label="Position value" value={dollarsPrettified(positionValueUsd)} />
        <SummaryStat label="Claimable fees" value={dollarsPrettified(claimableFeesUsd)} />
      </div>

      <div className="flex flex-col gap-2">
        <AmountRow symbol={token0Symbol} amount={amount0Display} label="You will receive" />
        <AmountRow symbol={token1Symbol} amount={amount1Display} />
      </div>

      <label className="flex items-start gap-2 text-xs text-t200">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => onToggleConfirmed(event.currentTarget.checked)}
          className="mt-0.5"
        />
        <span>I understand this action cannot be undone and will burn the position NFT.</span>
      </label>

      {nfpmTokenIdPending ? <NfpmPendingNotice actionLabel="Close position" /> : null}

      {submissionError ? (
        <p className="rounded-md border border-error-border/40 bg-error-background/10 px-3 py-2 text-xs text-error-text">
          {submissionError}
        </p>
      ) : null}

      <Button
        variant={"error"}
        size={"lg"}
        onClick={() => onChangeConfirmDialogOpen(true)}
        disabled={!canSubmit || isSubmitting}
        className={cn("w-full")}
      >
        {isSubmitting
          ? "Closing..."
          : nfpmTokenIdPending
            ? "Resolving NFP id…"
            : "Close position"}
      </Button>

      <Dialog open={confirmDialogOpen} onOpenChange={onChangeConfirmDialogOpen}>
        <DialogContent title="Close this position?">
          <DialogDescription>
            This will withdraw all liquidity, collect fees, and burn the NFP. You will receive{" "}
            {amount0Display} {token0Symbol} and {amount1Display} {token1Symbol} to your wallet
            (subject to {slippagePercent}% slippage).
          </DialogDescription>
          <DialogFooter>
            <Button
              variant={"ghost"}
              onClick={() => onChangeConfirmDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button variant={"error"} onClick={onSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Closing..." : "Confirm close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const SummaryStat = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col rounded-md bg-p300 border border-p300-light px-3 py-2">
    <span className="text-t300 text-[11px]">{label}</span>
    <span className="text-t100 text-sm font-medium">{value}</span>
  </div>
)

const AmountRow = ({
  symbol,
  amount,
  label,
}: {
  symbol: string
  amount: string
  label?: string
}) => (
  <div className="flex items-center justify-between rounded-md bg-p300 border border-p300-light px-3 py-2">
    <span className="text-t300 text-xs">{label ?? ""}</span>
    <div className="flex items-center gap-2">
      <span className="text-t100 text-sm font-medium">{amount}</span>
      <span className="text-t200 text-sm">{symbol}</span>
    </div>
  </div>
)
